/**
 * SoleadLab 2.0 — Truth Qualification API Client
 *
 * Thin fetch wrappers for the truth qualification endpoints.
 * All truth computation logic is backend-owned.
 */

import type {
  SiteTruthRow,
  TruthRecomputeResponse,
  TruthHistoryResponse,
  ConclusionFamily,
} from '@/types/truth';
import { apiFetch } from './apiFetch';

// ─── Truth API functions ─────────────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/truth/latest
 * Get the latest truth qualification for a specific conclusion family.
 */
export async function getTruthLatest(
  siteId: string,
  family: ConclusionFamily,
  scopeType = 'whole_site',
): Promise<{ truth_qualification: SiteTruthRow }> {
  const params = new URLSearchParams({ conclusion_family: family, scope_type: scopeType });
  return apiFetch(`/api/v2/sites/${siteId}/truth/latest?${params}`);
}

/**
 * GET /api/v2/sites/:siteId/truth/history
 * Get truth qualification history for a specific conclusion family.
 */
export async function getTruthHistory(
  siteId: string,
  family: ConclusionFamily,
  limit = 20,
  scopeType = 'whole_site',
): Promise<TruthHistoryResponse> {
  const params = new URLSearchParams({
    conclusion_family: family,
    scope_type: scopeType,
    limit: String(limit),
  });
  return apiFetch(`/api/v2/sites/${siteId}/truth/history?${params}`);
}

/**
 * POST /api/v2/sites/:siteId/truth/recompute
 * Trigger truth qualification recomputation.
 */
export async function triggerTruthRecompute(
  siteId: string,
  family: ConclusionFamily | 'all',
  scopeType = 'whole_site',
  scopeDefinition?: { label: string; asset_node_ids: string[] },
): Promise<TruthRecomputeResponse> {
  return apiFetch(`/api/v2/sites/${siteId}/truth/recompute`, {
    method: 'POST',
    body: JSON.stringify({
      conclusion_family: family,
      scope_type: scopeType,
      ...(scopeDefinition ? { scope_definition: scopeDefinition } : {}),
    }),
  }, 30_000); // longer timeout for compute
}
